import axios from 'axios';
import type {
  TreatmentSheet, TreatmentOrder, AdministrationGridResponse,
  VitalEntry, ClinicalNote, StallOccupancy, Stall,
  TreatmentSheetStatus, LocationType, ClinicalNoteType,
  ControlledSubstance, ControlledSubstanceLot, DeaSchedule,
  AdministerControlledSubstancePayload, DeaLogReconciliationRow,
  DuplicateDayResponse,
  TreatmentSheetBillingSummary,
} from '../types/treatmentSheet';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000',
  timeout: 30000,
});

// In dev-bypass mode, set the Authorization header at module load time so
// queries that fire before AuthProvider's request-interceptor mounts (e.g.
// react-query effects in child components, which run before parent effects
// under React's effect-ordering rules) still send a valid token. The
// AuthProvider request interceptor will overwrite this with a real
// Firebase token in production builds — same header, same shape.
if (process.env.REACT_APP_DEV_BYPASS_AUTH === 'true') {
  api.defaults.headers.common['Authorization'] = 'Bearer dev-bypass-token';
}

// Triggers a browser file download from a blob payload (used by DEA log PDF/CSV exports).
function triggerBlobDownload(data: BlobPart, mime: string, filename: string) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Envelope returned by backend list endpoints when called with `?page=&limit=`.
 * Endpoints fall back to a bare array when no pagination params are supplied
 * (preserves mobile/legacy compatibility).
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginationParams {
  page: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Release Notes API Types
export interface ReleaseNote {
  id: number;
  version: string;
  title: string;
  body: string;
  createdAt: string;
}

export interface ShouldShowReleaseNoteResponse {
  shouldShow: boolean;
  releaseNote?: ReleaseNote;
  // Every note since the user's last-seen one, oldest first; present when shouldShow.
  missedNotes?: ReleaseNote[];
}

// Release Notes API Functions
export const releaseNotesApi = {
  // Get the latest release note
  getLatest: (): Promise<ReleaseNote> => 
    api.get('/api/release-notes/latest').then(res => res.data),
  
  // Mark a release note as seen
  markSeen: (id: number): Promise<void> => 
    api.post(`/api/release-notes/${id}/seen`).then(() => undefined),
  
  // Check if user should see release note
  shouldShow: (): Promise<ShouldShowReleaseNoteResponse> => 
    api.get('/api/release-notes/should-show').then(res => res.data),
  
  // Get all release notes (public - for authenticated users)
  getAllPublic: (): Promise<ReleaseNote[]> => 
    api.get('/api/release-notes/public').then(res => res.data),
  
  // Admin: Create release note
  create: (data: { version: string; title: string; body: string }): Promise<ReleaseNote> =>
    api.post('/api/release-notes', data).then(res => res.data),
  
  // Admin: Get all release notes
  getAll: (): Promise<ReleaseNote[]> => 
    api.get('/api/release-notes').then(res => res.data),
};

// User API Functions
export const userApi = {
  // Send invitation email to a user
  sendInvitationEmail: (userId: string): Promise<{ success: boolean; message: string }> =>
    api.post(`/users/${userId}/send-invitation`).then(res => res.data),
};

// Client-related API Types
export enum ClientStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ON_HOLD = 'ON_HOLD'
}

export enum HorseClientRole {
  OWNER = 'OWNER',
  TRAINER = 'TRAINER'
}

export interface HorseWithRole {
  id: string;
  name: string;
  breed?: string;
  secondBreed?: string;
  color?: string;
  gender?: string;
  role: HorseClientRole;
  ownershipPercentage?: number | null;
}

// One additional contact person on a client (spouse, family member, the
// "call about the appointment" person). Display/communication only — NOT an
// owner; the bill-to identity stays Client.name/email/phone.
export interface ClientContact {
  name: string;
  role?: string;
  phone?: string;
  email?: string;
}

export interface Client {
  id: string;
  // Clinic-facing client ID ("C-00001"), assigned by the server on create; read-only.
  // Null until a client created offline has synced.
  clientNumber?: string | null;
  name: string;
  email?: string;
  phone?: string;
  billingAddress?: string;
  status: ClientStatus;
  smsEnabled: boolean;
  emailEnabled: boolean;
  // SMS consent (ADR-0022) — read-only on the wire; written ONLY via
  // clientApi.recordSmsConsent (server-stamped).
  smsConsentAt?: string | null;
  smsConsentSource?: string | null;
  smsConsentBy?: string | null;
  contacts?: ClientContact[];
  currentBalance: number;
  horses?: HorseWithRole[];
  horseCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Horse-related API Types
export interface Owner {
  name: string;
  email?: string;
  phone?: string;
  ownershipPercentage?: number;
}

export interface Trainer {
  name: string;
  email?: string;
  phone?: string;
}

export interface Horse {
  id: string;
  species?: string;
  name: string;               // Primary display name — the horse's barn (everyday) name
  registeredName?: string;    // Formal / registered name (e.g. "Dances With Fire")
  breed: string;
  dateOfBirth: string;
  gender: string;
  color: string;
  microchipId?: string;
  registrationNumber?: string;
  discipline?: string;        // Competition discipline (e.g. "Hunter/Jumper")
  insuranceCarrier?: string;
  insurancePolicyNumber?: string;
  peiNumber?: string;         // Permanent Equine Identification number
  causeOfDeath?: string;      // Deceased state only
  patientId?: string;  // Clinic-assigned human-facing identifier (e.g. "P-00001")
  secondBreed?: string;
  isMixed?: boolean;
  // Health snapshot fields (populated when the row is fetched with the
  // patient-list projection — nullable elsewhere).
  //
  // `cogginsTestDate` was here until 2026-09-08 and was a PHANTOM: zero writers
  // and zero readers anywhere in the repo, and no such column on `Horse` — it
  // read like a real Coggins field to anyone who found it. The Coggins date now
  // has exactly one home, `LabResult.expiryDate` (INV-102), and leaving a decoy
  // beside it is how the next person wires the wrong one. Type-only deletion:
  // no runtime, no contract.
  lastVaccination?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'DECEASED';
  dateOfDeath?: string;
  owners: Owner[];
  trainers?: Trainer[];
  location?: string;
  // FK to Barn — dual-written with `location` server-side (see barnsService).
  barnId?: string | null;
  // Populated on GET /horses/:id only (additive include) — the barn's address
  // for the profile-header Location block.
  barn?: {
    id: string;
    name: string;
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
  weight?: number;
  height?: number;
  // Backend stores as a JSON array of alert objects; consumers that need the
  // structured shape narrow this locally. Keeping it loose avoids drift.
  medicalAlerts?: any[];
  // Enriched fields returned by GET /horses (paginated). Single-horse GETs
  // don't populate these; the patients list endpoint computes them per page.
  lastVisitAt?: string | null;
  activeMedicationCount?: number;
  // Latest patient-photo URL (S3), resolved from the horse's attachments on
  // the paginated list so avatars need no per-row fetch. Null when no photo.
  photoUrl?: string | null;
}

export interface MedicalRecord {
  id: string;
  type: string;
  notes: string;
  date: string;
  horseId: string;
  horse?: Horse;
  parentId?: string;
  amendedAt?: string;
  isArchived?: boolean;
  archivedAt?: string;
  archivedBy?: string;
  category?: string;
  tags?: string[];
}

// Horse API Functions
export const horseApi = {
  // Legacy: get every horse in the clinic, uncapped. Prefer
  // `listPaginated` for UI list views on clinics with lots of data.
  list: (search?: string): Promise<Horse[]> => {
    const params = search ? { search } : {};
    return api.get('/horses', { params }).then(res => res.data);
  },

  /**
   * Paginated list of horses. Pass `page` (1-indexed) and optionally
   * `limit` (default 50, max 200). Returns `{ data, total, page, limit,
   * totalPages }`. Use this for PatientList and any other horse list UI.
   */
  listPaginated: (params: {
    page: number;
    limit?: number;
    search?: string;
    status?: 'ACTIVE' | 'INACTIVE' | 'DECEASED';
    location?: string;
  }): Promise<PaginatedResponse<Horse>> =>
    api
      .get('/horses', {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status,
          location: params.location,
        },
      })
      .then((res) => res.data),

  // Get specific horse
  get: (id: string): Promise<Horse> =>
    api.get(`/horses/${id}`).then(res => res.data),

  // Get medical records for a horse
  getMedicalRecords: (id: string, includeArchived = false): Promise<MedicalRecord[]> =>
    api.get('/medical-records', {
      params: {
        horseId: id,
        includeArchived: includeArchived.toString(),
      },
    }).then(res => res.data),

  /**
   * Clinic-wide list of horses with at least one critical alert. Server-side
   * filter — does NOT respect page boundaries. The Patients-page insights
   * rail uses this so a flagged horse on page 17 still surfaces.
   */
  listFlagged: (): Promise<Array<Pick<Horse, 'id' | 'name' | 'patientId' | 'status' | 'medicalAlerts'> & { criticalAlerts: any[] }>> =>
    api.get('/horses/flagged').then(res => res.data),

  /**
   * Distinct barn / stabling locations across the clinic's horses. Powers the
   * barn picker on the patient form (so vets reuse existing barn names instead
   * of re-typing — keeps the Barn Operations grouping clean) and the location
   * filter.
   */
  listLocations: (): Promise<string[]> =>
    api.get('/horses/locations').then(res => res.data),
};

// Client API Functions
export const clientApi = {
  /** SMS consent is a server-stamped action, never part of the client PATCH
   *  (ADR-0022). Online-only — consent cannot ride the offline queue. */
  recordSmsConsent: (
    id: string,
    granted: boolean,
    source?: string,
  ): Promise<Client> =>
    api.post(`/clients/${id}/sms-consent`, { granted, source }).then((res) => res.data),

  // Legacy: get all clients (capped at 500 rows by backend). Prefer
  // `listPaginated` for UI list views on clinics with lots of data.
  list: (search?: string, status?: ClientStatus): Promise<Client[]> => {
    const params: any = {};
    if (search) params.search = search;
    if (status) params.status = status;
    return api.get('/clients', { params }).then(res => res.data);
  },

  /**
   * Paginated list of clients. Server computes balances only for the
   * current page — critical for clinics with many clients.
   */
  listPaginated: (params: {
    page: number;
    limit?: number;
    search?: string;
    status?: ClientStatus;
  }): Promise<PaginatedResponse<Client>> =>
    api
      .get('/clients', {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status,
        },
      })
      .then((res) => res.data),
  
  // Get specific client with horses and roles
  get: (id: string): Promise<Client> => 
    api.get(`/clients/${id}`).then(res => res.data),
  
  // Create a new client
  create: (data: {
    name: string;
    email?: string;
    phone?: string;
    billingAddress?: string;
    status?: ClientStatus;
    smsEnabled?: boolean;
    emailEnabled?: boolean;
    contacts?: ClientContact[];
    horseIds?: { horseId: string; role: HorseClientRole }[];
  }): Promise<Client> =>
    api.post('/clients', data).then(res => res.data),

  // Update a client
  update: (id: string, data: {
    name?: string;
    email?: string;
    phone?: string;
    billingAddress?: string;
    status?: ClientStatus;
    smsEnabled?: boolean;
    emailEnabled?: boolean;
    contacts?: ClientContact[];
  }): Promise<Client> =>
    api.put(`/clients/${id}`, data).then(res => res.data),
  
  // Get delete info (horses affected, primary owner warnings). hasHistory: invoices, credit or
  // statements, so the server refuses the delete and the clinic marks the client inactive (fix 6).
  getDeleteInfo: (id: string): Promise<{
    clientName: string;
    horseCount: number;
    horses: Array<{ id: string; name: string; isPrimaryOwner: boolean }>;
    hasHistory: boolean;
  }> =>
    api.get(`/clients/${id}/delete-info`).then(res => res.data),
  
  // Delete a client
  delete: (id: string): Promise<Client> =>
    api.delete(`/clients/${id}`).then(res => res.data),
  
  // Find or create a client (for autocomplete)
  findOrCreate: (data: {
    name: string;
    email?: string;
    phone?: string;
  }): Promise<Client> =>
    api.post('/clients/find-or-create', data).then(res => res.data),
  
  // Link client to horse with a role. For OWNER links on a horse that already
  // has owners, `allocations` states every owner's percentage (existing + new,
  // must sum to 100) — HorseClient percentages drive invoice splits, so the
  // server rebalances all owners atomically.
  linkHorse: (
    clientId: string,
    horseId: string,
    role: HorseClientRole,
    allocations?: Array<{ clientId: string; ownershipPercentage: number }>,
  ): Promise<{ message: string }> =>
    api.post(`/clients/${clientId}/horses/${horseId}/role`, { role, allocations }).then(res => res.data),
  
  // Unlink client from horse (specific role)
  unlinkHorse: (clientId: string, horseId: string, role: HorseClientRole): Promise<{ message: string }> =>
    api.delete(`/clients/${clientId}/horses/${horseId}/role/${role}`).then(res => res.data),
};

// ─── Client Invoice / Payment Method Types ───────────────────────────────────

export interface StatementLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface OpenInvoiceRow {
  id: string;
  invoiceNumber: string;
  description: string | null;
  issuedAt: string | null;
  billedTotal: number;
  tax: number;
  // Equals billedTotal — a statement only consolidates invoices the client owes
  // in full (co-owned invoices are split upstream into per-owner invoices).
  clientOwes: number;
  billedToYouDirectly: boolean;
  lineItems: StatementLineItem[];
}

export interface OpenInvoiceHorse {
  horse: { id: string; name: string; location: string | null };
  ownershipPercentage: number | null;
  invoices: OpenInvoiceRow[];
}

// Co-owned invoices the client part-owns but which haven't been split per owner —
// not directly billable on a statement; the client must "Bill to owners" first.
export interface NeedsSplitInvoice {
  id: string;
  invoiceNumber: string;
  description: string | null;
  horse: { id: string; name: string; location: string | null };
  billedTotal: number;
}

export interface ClientPaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  type: string;
  cardBrand: string | null;
  cardLast4: string | null;
  isDefault: boolean;
}

export interface ClientInvoicePaymentRecord {
  id: string;
  method: string;
  amount: number;
  referenceNote: string | null;
}

export interface ClientInvoiceRecord {
  id: string;
  invoiceNumber: string;
  status: string;
  totalAmount: number;
  sourceInvoiceIds: string[];
  createdAt: string;
  client: { id: string; name: string; email?: string };
  payments: ClientInvoicePaymentRecord[];
}

// Row shape for the statements list (lighter than the full record).
export interface ClientInvoiceSummary {
  id: string;
  invoiceNumber: string;
  status: string;
  // 'manual' | 'auto_monthly' — auto rows come from the monthly statement run.
  origin?: string;
  // "YYYY-MM" clinic-local month an auto statement covers; null for manual.
  statementPeriod?: string | null;
  totalAmount: number;
  sourceInvoiceIds: string[];
  createdAt: string;
  client: { id: string; name: string } | null;
}

// Clinic-level monthly-statement settings (Settings → Invoicing).
export interface StatementSettings {
  monthlyStatementsEnabled: boolean;
  statementCoverLetter: string | null;
  // GST/HST registration number printed on invoice + statement PDF headers
  taxRegistrationNumber: string | null;
  // Display-only payment-terms line rendered under statement totals
  statementTerms: string | null;
}

export interface PaymentEntry {
  method: string;
  amount: number;
  referenceNote?: string;
  paymentMethodId?: string;
}

// Invoice Statistics Types
export interface InvoiceStats {
  balance: number;
  openInvoiceCount: number;
  lastInvoiceDate: string | null;
  totalInvoiceCount: number;
  /**
   * A5c, additive: money the client has SENT that has not settled (an ACH
   * clearing), ALREADY subtracted from `balance` above. Optional because the
   * offline replica arm and any older server do not supply it.
   */
  processingAmount?: number;
}

/**
 * Aggregate stats computed server-side for the Billing page header tiles.
 * Replaces the old pattern of fetching every invoice and summing in JS.
 */
export interface ClinicInvoiceStats {
  totalRevenue: number;
  /** Net open balance NOT yet past due. Same value as `outstandingAmount`. */
  pendingAmount: number;
  /** Net open balance past due in the CLINIC's zone. Same as `pastDueAmount`. */
  overdueAmount: number;
  averageInvoice: number;
  pendingCount: number;
  overdueCount: number;
  totalCount: number;
  paidAmount30Days: number;
  paidCount30Days: number;
  /**
   * A5b additive open-AR plane (server always sends these since the
   * `getClinicInvoiceStats` rewrite): net open balance NOT yet past due, net
   * open balance past due (`dueDate` in the clinic's zone), and the count of
   * open invoices that still owe something — open statuses INCLUDING
   * `partially_paid`, succeeded+processing netted per invoice.
   *
   * ⚠ The two AMOUNTS are DISJOINT buckets — total open AR is their SUM.
   * `openInvoiceCount`, by contrast, IS the both-bucket total.
   */
  outstandingAmount: number;
  pastDueAmount: number;
  /** In-flight ACH across the open invoices, ALREADY deducted from the two above. */
  processingAmount: number;
  openInvoiceCount: number;
}

// Billing API Functions
export const billingApi = {
  // Get invoice statistics for a specific horse
  getHorseStats: (horseId: string): Promise<InvoiceStats> =>
    api.get(`/billing/stats/horse/${horseId}`).then(res => res.data),

  // Get invoice statistics for a client (across all their horses)
  getClientStats: (clientId: string): Promise<InvoiceStats> =>
    api.get(`/billing/stats/client/${clientId}`).then(res => res.data),

  /** Clinic-wide invoice stats for the Billing page header tiles. */
  getClinicStats: (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ClinicInvoiceStats> =>
    api.get('/billing/stats/clinic', { params }).then((res) => res.data),

  /**
   * Paginated invoices list. Supports `status`, `search`, date range,
   * and per-horse/per-client scoping. Use this from the Billing page
   * instead of fetching every invoice into memory.
   */
  listInvoicesPaginated: (params: {
    page: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    horseId?: string;
    clientId?: string;
  }): Promise<PaginatedResponse<any>> =>
    api
      .get('/billing', {
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          startDate: params.startDate,
          endDate: params.endDate,
          search: params.search || undefined,
          horseId: params.horseId,
          clientId: params.clientId,
        },
      })
      .then((res) => res.data),
};

// Medical Records API Functions
export const medicalRecordsApi = {
  // Create a new medical record (clinic context is derived from JWT)
  create: (data: Partial<MedicalRecord> & { category?: string; tags?: string[] }): Promise<any> => {
    return api
      .post('/medical-records', {
        horseId: data.horseId,
        type: data.type,
        notes: data.notes,
        date: data.date,
        category: data.category,
        tags: data.tags,
      })
      .then((res) => res.data);
  },

  // Update a medical record
  update: (id: string, data: Partial<MedicalRecord> & { category?: string; tags?: string[] }): Promise<MedicalRecord> => {
    const { horseId, ...updateData } = data; // horseId is immutable

    return api
      .put(`/medical-records/${id}`, {
        type: updateData.type,
        notes: updateData.notes,
        date: updateData.date,
        category: updateData.category,
        tags: updateData.tags,
      })
      .then((res) => res.data);
  },
  
  // Archive a medical record (soft delete)
  archive: (id: string): Promise<MedicalRecord> =>
    api.post(`/medical-records/${id}/archive`).then((res) => res.data),
  
  // Restore an archived medical record
  restore: (id: string): Promise<MedicalRecord> =>
    api.post(`/medical-records/${id}/restore`).then((res) => res.data),
  
  // Get all medical records with optional archived filter
  getAll: (includeArchived = false): Promise<MedicalRecord[]> =>
    api.get('/medical-records', { 
      params: { includeArchived: includeArchived.toString() }
    }).then((res) => res.data),
};

// ─── AI Treatment Order Types ────────────────────────────────────────────────

export interface SuggestedOrder {
  name: string;
  category: string;
  dose?: string;
  route?: string;
  frequency: string;
  durationDays?: number;
  specialInstructions?: string;
  warnings: string[];
  suggestedCanonicalName?: string;
  confidence: number;
}

// ─── Treatment Sheet API ──────────────────────────────────────────────────────

export const treatmentSheetApi = {
  // Sheets
  listSheets: (params?: {
    status?: TreatmentSheetStatus;
    locationType?: LocationType;
    horseId?: string;
  }): Promise<TreatmentSheet[]> =>
    api.get('/treatment-sheets', { params }).then((res) => res.data),

  getSheet: (id: string): Promise<TreatmentSheet> =>
    api.get(`/treatment-sheets/${id}`).then((res) => res.data),

  createSheet: (data: any): Promise<TreatmentSheet> =>
    api.post('/treatment-sheets', data).then((res) => res.data),

  updateSheet: (id: string, data: any): Promise<TreatmentSheet> =>
    api.patch(`/treatment-sheets/${id}`, data).then((res) => res.data),

  deleteSheet: (id: string): Promise<void> =>
    api.delete(`/treatment-sheets/${id}`).then(() => undefined),

  /** What this admission has cost so far, broken down by service day. */
  getBillingSummary: (id: string): Promise<TreatmentSheetBillingSummary> =>
    api.get(`/treatment-sheets/${id}/billing-summary`).then((res) => res.data),

  getHorseSheets: (horseId: string): Promise<TreatmentSheet[]> =>
    api.get(`/horses/${horseId}/treatment-sheets`).then((res) => res.data),

  // Orders
  listOrders: (sheetId: string): Promise<TreatmentOrder[]> =>
    api.get(`/treatment-sheets/${sheetId}/orders`).then((res) => res.data),

  /**
   * What this order would be billed at, resolved by the SAME server-side lookup
   * the create path uses. null = nothing in the catalog matches that name.
   */
  previewOrderPrice: (name: string, category: string): Promise<{ unitPrice: number | null }> =>
    api
      .get('/treatment-orders/price-preview', { params: { name, category } })
      .then((res) => res.data),

  createOrder: (sheetId: string, data: any): Promise<TreatmentOrder> =>
    api.post(`/treatment-sheets/${sheetId}/orders`, data).then((res) => res.data),

  updateOrder: (orderId: string, data: any): Promise<TreatmentOrder> =>
    api.patch(`/treatment-orders/${orderId}`, data).then((res) => res.data),

  discontinueOrder: (orderId: string): Promise<void> =>
    api.delete(`/treatment-orders/${orderId}`).then(() => undefined),

  // Copy one day's active orders onto a chosen day (day-scoped carry-forward;
  // targetDate omitted = today).
  duplicateDay: (
    sheetId: string,
    data: { sourceDate: string; targetDate?: string; timeZone?: string },
  ): Promise<DuplicateDayResponse> =>
    api.post(`/treatment-sheets/${sheetId}/orders/duplicate-day`, data).then((res) => res.data),

  // Administration Grid
  getAdministrationGrid: (
    sheetId: string,
    params?: { windowStart?: string; windowEnd?: string },
  ): Promise<AdministrationGridResponse> =>
    api.get(`/treatment-sheets/${sheetId}/administrations`, { params }).then((res) => res.data),

  administerTreatment: (orderId: string, data: { scheduledAt: string; actualDose?: string; notes?: string }): Promise<void> =>
    api.post(`/treatment-orders/${orderId}/administer`, data).then(() => undefined),

  administerControlledSubstance: (orderId: string, data: AdministerControlledSubstancePayload): Promise<void> =>
    api.post(`/treatment-orders/${orderId}/administer`, data).then(() => undefined),

  updateAdministrationLog: (logId: string, data: { status?: string; actualDose?: string; notes?: string }): Promise<void> =>
    api.patch(`/administration-logs/${logId}`, data).then(() => undefined),

  // Vitals
  listVitals: (sheetId: string): Promise<VitalEntry[]> =>
    api.get(`/treatment-sheets/${sheetId}/vitals`).then((res) => res.data),

  logVitals: (sheetId: string, data: any): Promise<VitalEntry> =>
    api.post(`/treatment-sheets/${sheetId}/vitals`, data).then((res) => res.data),

  // Clinical Notes
  listNotes: (
    sheetId: string,
    params?: { noteType?: string },
  ): Promise<ClinicalNote[]> =>
    api.get(`/treatment-sheets/${sheetId}/notes`, { params }).then((res) => res.data),

  addNote: (sheetId: string, data: { noteType?: ClinicalNoteType; body: string }): Promise<ClinicalNote> =>
    api.post(`/treatment-sheets/${sheetId}/notes`, data).then((res) => res.data),

  // Stalls / managed locations
  listStalls: (type?: LocationType): Promise<Stall[]> =>
    api.get('/stalls', { params: type ? { type } : undefined }).then((res) => res.data),

  getOccupancy: (): Promise<StallOccupancy[]> =>
    api.get('/stalls/occupancy').then((res) => res.data),

  createStall: (data: { name: string; type?: LocationType; section?: string }): Promise<Stall> =>
    api.post('/stalls', data).then((res) => res.data),

  updateStall: (id: string, data: any): Promise<Stall> =>
    api.patch(`/stalls/${id}`, data).then((res) => res.data),

  deleteStall: (id: string): Promise<void> =>
    api.delete(`/stalls/${id}`).then((res) => res.data),

  generateOrders: (body: {
    transcript: string;
    sheetId: string;
    horseName?: string;
    horseBreed?: string;
    horseWeightKg?: number;
  }): Promise<{ success: boolean; message?: string; orders: SuggestedOrder[] }> =>
    api.post('/ai/generate-treatment-orders', body).then((res) => res.data),
};

// ─── DEA Logs API ─────────────────────────────────────────────────────────────

export interface DeaLogQuery {
  schedule?: DeaSchedule;
  from?: string;
  to?: string;
  substanceId?: string;
  horseId?: string;
  vetId?: string;
  witnessId?: string;
}

// Shape returned by GET /dea-logs/administrations (unified ledger, ADR-0010):
// substance / patient / orderName are top-level; lot carries only id+number+exp.
export interface AdministrationLogRecord {
  id: string;
  source?: string;
  eventType?: string;
  // DEA auto-log (ADR-0014): entry created automatically from a dispense/
  // charge-out; pending=true until vet+witness attestation signs it.
  pending?: boolean;
  autoLogged?: boolean;
  sourceType?: string | null;
  administeredAt: string | null;
  scheduledAt?: string;
  signedAt?: string | null;
  notes?: string | null;
  quantityAdministered?: string | number | null;
  quantityWasted?: string | number | null;
  wasteReason?: string | null;
  prescribingVetDeaNumber?: string | null;
  substance?: {
    id: string;
    genericName: string;
    brandName?: string | null;
    deaSchedule: DeaSchedule;
    strength?: string;
    unit?: string;
    dosageForm?: string;
    ndcCode?: string | null;
    /** When false the row is authority-pending: a vet can attest without a witness. */
    requiresWitness?: boolean;
  } | null;
  lot?: { id: string; lotNumber: string; expirationDate: string } | null;
  patient?: { id: string; name: string; microchipId?: string | null; breed?: string } | null;
  administeredBy?: { id: string; name: string } | null;
  witness?: { id: string; name: string } | null;
  orderName?: string;
  treatmentSheetId?: string;
}

export interface DeaAdministrationsSeparated {
  scheduleII: AdministrationLogRecord[];
  scheduleIIIthroughV: AdministrationLogRecord[];
}

// GET /dea-logs/unmatched-activity — controlled activity the auto-log could
// NOT record (ambiguous registry match, insufficient lot balance). Fail-loud.
export interface UnmatchedCsActivityRow {
  movementId: string;
  occurredAt: string;
  name: string;
  quantity: number;
  sourceType?: string | null;
  sourceId?: string | null;
  substance: {
    id: string;
    genericName: string;
    brandName?: string | null;
    deaSchedule: DeaSchedule;
    unit: string;
  };
}

// GET /dea-logs/substance-match — bottle-picker lookup for dispense dialogs.
// Same single-match semantics as the DEA auto-log (ambiguous = matched:false);
// lots come back FIFO-ordered. NOTE: the server auto-pick is the first lot
// that can COVER the dispense quantity — sufficiency, not just order, is part
// of the lockstep (DeaBottleSelect preselects accordingly).
export interface DeaSubstanceMatch {
  matched: boolean;
  ambiguous: boolean;
  substance?: {
    id: string;
    genericName: string;
    brandName?: string | null;
    deaSchedule: DeaSchedule;
    unit: string;
    requiresWitness: boolean;
  };
  lots: Array<{
    id: string;
    lotNumber: string;
    expirationDate: string;
    quantityRemaining: string | number;
  }>;
}

export const deaLogsApi = {
  // Substance catalog
  listSubstances: (includeInactive = false): Promise<ControlledSubstance[]> =>
    api.get('/dea-logs/substances', { params: { includeInactive } }).then((res) => res.data),

  createSubstance: (data: Partial<ControlledSubstance>): Promise<ControlledSubstance> =>
    api.post('/dea-logs/substances', data).then((res) => res.data),

  deactivateSubstance: (id: string): Promise<ControlledSubstance> =>
    api.patch(`/dea-logs/substances/${id}/deactivate`).then((res) => res.data),

  // Lots
  listLots: (substanceId?: string, includeDisposed = false): Promise<ControlledSubstanceLot[]> =>
    api.get('/dea-logs/lots', { params: { substanceId, includeDisposed } }).then((res) => res.data),

  matchSubstance: (name: string): Promise<DeaSubstanceMatch> =>
    api.get('/dea-logs/substance-match', { params: { name } }).then((res) => res.data),

  createLot: (data: {
    substanceId: string;
    lotNumber: string;
    expirationDate: string;
    quantityReceived: number;
    receivedDate?: string;
    receivedFromSupplier?: string;
    form222Number?: string;
  }): Promise<ControlledSubstanceLot> =>
    api.post('/dea-logs/lots', data).then((res) => res.data),

  // Administrations
  listAdministrations: (query: DeaLogQuery = {}): Promise<DeaAdministrationsSeparated> =>
    api.get('/dea-logs/administrations', { params: query }).then((res) => res.data),

  downloadPdf: async (query: DeaLogQuery = {}): Promise<void> => {
    const res = await api.get('/dea-logs/administrations/export.pdf', {
      params: query, responseType: 'blob',
    });
    triggerBlobDownload(res.data, 'application/pdf', 'dea-administration-log.pdf');
  },

  downloadCsv: async (query: DeaLogQuery = {}): Promise<void> => {
    const res = await api.get('/dea-logs/administrations/export.csv', {
      params: query, responseType: 'blob',
    });
    triggerBlobDownload(res.data, 'text/csv', 'dea-administration-log.csv');
  },

  // Reconciliation
  getReconciliation: (): Promise<DeaLogReconciliationRow[]> =>
    api.get('/dea-logs/inventory/reconciliation').then((res) => res.data),

  // ─── Auto-log attestation queue (ADR-0014). Online-only, like all DEA
  // writes — a network failure surfaces as an error, never queues offline. ───
  listPendingAttestations: (): Promise<AdministrationLogRecord[]> =>
    api.get('/dea-logs/pending-attestations').then((res) => res.data),

  attestAdministration: (
    id: string,
    data: { witnessId?: string; witnessAttests?: boolean; notes?: string },
  ): Promise<AdministrationLogRecord> =>
    api.post(`/dea-logs/administrations/${id}/attest`, data).then((res) => res.data),

  getUnmatchedActivity: (days?: number): Promise<UnmatchedCsActivityRow[]> =>
    api.get('/dea-logs/unmatched-activity', { params: days ? { days } : {} }).then((res) => res.data),
};

// Appointment-related API Types
export interface AppointmentListItem {
  id: string;
  date: string;
  duration?: number;
  type: string;
  location: string;
  // Subject: a patient (horse), an owner (client), or a barn. At least one
  // is set; horse-less rows exist since the owner/barn booking feature.
  horseId: string | null;
  clientId?: string | null;
  notes?: string;
  status?: string;
  assignedVetId?: string | null;
  // Barn-visit grouping: N per-horse appointments of one visit share this id.
  visitGroupId?: string | null;
  barnId?: string | null;
  horse?: { id: string; name: string; breed?: string } | null;
  client?: { id: string; name: string; phone?: string | null } | null;
  barn?: { id: string; name: string } | null;
  assignedVet?: { id: string; name: string; firstName?: string; lastName?: string };
  invoice?: { id: string; invoiceNumber: string } | null;
}

// Appointment API Functions
export const appointmentApi = {
  /**
   * Legacy: fetch appointments (capped at 500 rows by backend).
   * Prefer `listPaginated` with a date range for list UIs.
   */
  list: (params?: {
    veterinarianId?: string;
    horseId?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    visitGroupId?: string;
  }): Promise<AppointmentListItem[]> =>
    api.get('/appointments', { params }).then((res) => res.data),

  /**
   * Schedule one barn visit covering N horses (all-or-nothing). Returns the
   * shared visitGroupId + the created per-horse appointments. With
   * horseIds: [] it books a single barn-level appointment instead —
   * visitGroupId comes back null (calendar-only, no visit workspace).
   */
  createBarnVisit: (payload: {
    barnId: string;
    horseIds: string[];
    date: string;
    timeZone: string;
    duration?: number;
    type?: string;
    assignedVetId?: string;
    equipment?: Array<{ id: string; startTime: string; endTime: string }>;
    notes?: string;
    /** tentative | scheduled (Confirmed); the server defaults to scheduled. */
    status?: string;
  }): Promise<{ visitGroupId: string | null; appointments: AppointmentListItem[] }> =>
    api.post('/appointments/barn-visit', payload).then((res) => res.data),

  /**
   * Paginated list of appointments with optional date-range filtering.
   * Dashboard/Appointments/Billing pages should always pass a date range
   * so the working set stays small.
   */
  listPaginated: (params: {
    page: number;
    limit?: number;
    veterinarianId?: string;
    horseId?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
  }): Promise<PaginatedResponse<AppointmentListItem>> =>
    api.get('/appointments', { params }).then((res) => res.data),
};

// Client payment methods — saved cards on file
export const paymentMethodsApi = {
  // Backend route is GET /clients/:id/payment-methods (same one EnhancedPaymentDialog
  // uses). The old /payment-methods/client/:id path does not exist → 404 → the
  // statement wizard's saved-card list was always empty and "On File" never showed.
  list: (clientId: string): Promise<ClientPaymentMethod[]> =>
    api.get(`/clients/${clientId}/payment-methods`).then(res => res.data),
};

// Batch payments
export const batchPaymentApi = {
  record: (payload: {
    paymentType: string;
    paymentDate?: string;
    referenceNote?: string;
    allocations: Array<{ invoiceId: string; amount: number; referenceNote?: string }>;
  }) => api.post('/payments/record-batch', payload).then((res) => res.data),

  updateReference: (paymentId: string, referenceNote: string | null) =>
    api.patch(`/payments/${paymentId}/reference`, { referenceNote }).then((res) => res.data),
};

// BILL-CRED: client account-credit ledger (issue / view / apply at settlement).
// Amounts are DOLLARS on the wire; the ledger stores integer cents.
export interface CreditLedgerEntry {
  id: string;
  clientId: string;
  amountCents: number;
  type: 'issued' | 'applied';
  source: string;
  reason?: string | null;
  createdBy: string;
  linkedInvoiceId?: string | null;
  paymentId?: string | null;
  createdAt: string;
}

export const creditsApi = {
  issue: (payload: {
    clientId: string;
    amount: number;
    source: 'overpayment' | 'void_in_lieu' | 'goodwill' | 'prepayment';
    reason?: string;
    linkedInvoiceId?: string;
    // idempotency key (uuid per dialog-open): duplicate submits return the original entry
    requestId?: string;
  }): Promise<{ entry: CreditLedgerEntry; balanceCents: number; balance: number }> =>
    api.post('/credits', payload).then((res) => res.data),

  ledger: (clientId: string): Promise<{ clientId: string; balanceCents: number; balance: number; entries: CreditLedgerEntry[] }> =>
    api.get(`/credits/client/${clientId}`).then((res) => res.data),

  apply: (payload: {
    clientId: string;
    allocations: Array<{ invoiceId: string; amount: number }>;
    // idempotency key (uuid per attempt-set): a lost-response retry replays instead of spending
    // the client's credit twice; same key with CHANGED allocations is a 409 (credit-integrity F3)
    requestId?: string;
  }): Promise<{ batchId: string; count: number; totalApplied: number; balance: number }> =>
    api.post('/credits/apply', payload).then((res) => res.data),
};

// Client Invoices
export const clientInvoiceApi = {
  getOpenInvoices: (clientId: string): Promise<{ client: { id: string; name: string; email?: string }; horses: OpenInvoiceHorse[]; needsSplit: NeedsSplitInvoice[] }> =>
    api.get(`/billing/open-invoices/${clientId}`).then(res => res.data),

  create: (clientId: string, sourceInvoiceIds: string[]): Promise<ClientInvoiceRecord> =>
    api.post('/billing/client-invoices', { clientId, sourceInvoiceIds }).then(res => res.data),

  // List the clinic's statements (most recent first) for a findable list view.
  // `clientId` scopes them SERVER-side: the endpoint caps at take: 300, so
  // filtering the page in the browser would hide a client's older statements.
  list: (clientId?: string): Promise<ClientInvoiceSummary[]> =>
    api
      .get('/billing/client-invoices', clientId ? { params: { clientId } } : undefined)
      .then(res => res.data),

  get: (id: string): Promise<ClientInvoiceRecord> =>
    api.get(`/billing/client-invoices/${id}`).then(res => res.data),

  recordPayments: (id: string, payments: PaymentEntry[]): Promise<ClientInvoiceRecord> =>
    api.post(`/billing/client-invoices/${id}/payments`, { payments }).then(res => res.data),

  markSent: (id: string): Promise<ClientInvoiceRecord> =>
    api.post(`/billing/client-invoices/${id}/mark-sent`).then(res => res.data),

  getPdf: (id: string): Promise<Blob> =>
    api.get(`/billing/client-invoices/${id}/pdf`, { responseType: 'blob' }).then(res => res.data),

  // Generates the branded statement PDF server-side, emails it to the client
  // (defaults to the client's email), and marks the statement sent.
  email: (id: string, recipientEmail?: string): Promise<{ message: string }> =>
    api.post(`/billing/client-invoices/${id}/email`, { recipientEmail }).then(res => res.data),

  // Charge the client's saved card/bank for the whole statement balance (Stripe).
  // status: 'succeeded' (card) | 'processing' (ACH) | 'requires_action'.
  charge: (id: string, paymentMethodId: string): Promise<{ status: string; statementId: string; paymentIntentId: string }> =>
    api.post(`/billing/client-invoices/${id}/charge`, { paymentMethodId }).then(res => res.data),

  // Create a Stripe PaymentIntent for the whole statement balance (inline new-card
  // charge via Stripe Elements). `method` is the card type the user picked
  // (credit_card/debit_card) so the receipt records it. Returns the client secret
  // + connected account id.
  createPaymentIntent: (id: string, method?: string): Promise<{ clientSecret: string; paymentIntentId: string; stripeAccountId: string }> =>
    api.post(`/billing/client-invoices/${id}/payment-intent`, { method }).then(res => res.data),
};

// Monthly-statement settings (per-clinic opt-in + cover letter).
export const statementSettingsApi = {
  get: (): Promise<StatementSettings> =>
    api.get('/billing/statement-settings').then(res => res.data),

  update: (data: {
    monthlyStatementsEnabled?: boolean;
    statementCoverLetter?: string;
    taxRegistrationNumber?: string;
    statementTerms?: string;
  }): Promise<StatementSettings> =>
    api.put('/billing/statement-settings', data).then(res => res.data),
};

/** Settings → Invoicing: the note every new invoice starts with (ADR-0044). */
export interface InvoiceSettings {
  defaultClientNote: string | null;
}

export const invoiceSettingsApi = {
  get: (): Promise<InvoiceSettings> =>
    api.get('/billing/invoice-settings').then(res => res.data),

  update: (data: { defaultClientNote: string }): Promise<InvoiceSettings> =>
    api.put('/billing/invoice-settings', data).then(res => res.data),
};

export default api;
